import { useCallback, useMemo } from 'react';
import {
  useAppSelector,
  useAppDispatch,
  relatedQuestionsState,
  toggleRelatesQuestionsVisible,
} from 'src/store';
import {
  isChitChatCard,
  isImageGenerationCard,
  isResearchTaskCreationCard,
  Message,
  TaskSkill,
} from 'src/types';
import { useSession } from '../useSession';

export const useRelatedQuestions = () => {
  const { isOpenTier, isFreeTier, isStandardTier, isTeamsStandardTier } =
    useSession();

  const { relatedQuestionsVisibleMap } = useAppSelector(relatedQuestionsState);
  const dispatch = useAppDispatch();

  const onToggleRelatedQuestionsVisibility = useCallback(
    (messageId: string) => {
      dispatch(toggleRelatesQuestionsVisible(messageId));
    },
    [dispatch],
  );

  const isRelatedQuestionsVisible = useCallback(
    (messageId?: string | null) => {
      return messageId ? !!relatedQuestionsVisibleMap[messageId] : false;
    },
    [relatedQuestionsVisibleMap],
  );

  const getRelatedQuestions = useCallback(
    (payload: Message['payload']) => {
      if (isResearchTaskCreationCard(payload)) {
        return payload?.research_card?.data?.related_questions;
      }

      if (
        isChitChatCard(payload) &&
        payload.data?.skill !== TaskSkill.DOCUMENT_QA
      ) {
        return payload?.data?.related_questions;
      }

      if (
        isChitChatCard(payload) &&
        payload.data?.skill === TaskSkill.DOCUMENT_QA &&
        !(isFreeTier || isStandardTier || isOpenTier || isTeamsStandardTier)
      ) {
        return payload?.data?.related_questions;
      }

      if (isImageGenerationCard(payload)) {
        return payload?.related_questions;
      }
    },
    [isOpenTier, isStandardTier, isFreeTier, isTeamsStandardTier],
  );

  return useMemo(
    () => ({
      relatedQuestionsVisibleMap,
      getRelatedQuestions,
      isRelatedQuestionsVisible,
      onToggleRelatedQuestionsVisibility,
    }),
    [
      getRelatedQuestions,
      relatedQuestionsVisibleMap,
      isRelatedQuestionsVisible,
      onToggleRelatedQuestionsVisibility,
    ],
  );
};
